import { NavLink, useParams } from 'react-router-dom';
import {
  LayoutDashboard,
  ClipboardList,
  Building2,
  ParkingSquare,
  Users,
  Settings,
  ShieldCheck,
  Plus,
  Bot,
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useAuth } from '@/hooks/useAuth';
import { useBranding } from '@/hooks/useBranding';
import logoImg from '@/assets/arlo_auto_logo_principal.png';
import simboloImg from '@/assets/arlo_auto_simbolo.png';

interface NavItem {
  label: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number;
}

export function Sidebar({ collapsed = false }: { collapsed?: boolean }) {
  const { isPlatformAdmin, isClientAdmin, isCompanyAdmin, canManageUsers, canCreateInspections, canReviewInspections } = useAuth();
  const branding = useBranding();

  const { tenantSlug } = useParams<{ tenantSlug?: string }>();
  const prefix = tenantSlug ? `/${tenantSlug}` : '';

  const platformNav: NavItem[] = [
    { label: 'Painel', to: '/admin/tenants', icon: LayoutDashboard },
    { label: 'Clientes (Tenants)', to: '/admin/tenants', icon: ShieldCheck },
    { label: 'Usuários', to: '/admin/users', icon: Users },
    { label: 'Governança de IA', to: '/admin/ai', icon: Bot },
  ];

  const operationsNav: NavItem[] = [
    { label: 'Painel', to: `${prefix}/dashboard`, icon: LayoutDashboard },
    ...(canReviewInspections() ? [{ label: 'Vistorias', to: `${prefix}/inspections`, icon: ClipboardList }] : []),
    ...(canCreateInspections() ? [{ label: 'Nova Vistoria', to: `${prefix}/inspect/new`, icon: Plus }] : []),
  ];

  const companyAdminNav: NavItem[] = [
    { label: 'Painel', to: `${prefix}/dashboard`, icon: LayoutDashboard },
    { label: 'Vistorias', to: `${prefix}/inspections`, icon: ClipboardList },
    { label: 'Nova Vistoria', to: `${prefix}/inspect/new`, icon: Plus },
    { label: 'Pátios', to: `${prefix}/parking-lots`, icon: ParkingSquare },
    ...(canManageUsers() ? [{ label: 'Usuários', to: `${prefix}/users`, icon: Users }] : []),
  ];

  const adminNav: NavItem[] = [
    { label: 'Painel', to: `${prefix}/dashboard`, icon: LayoutDashboard },
    { label: 'Vistorias', to: `${prefix}/inspections`, icon: ClipboardList },
    { label: 'Empresas', to: `${prefix}/companies`, icon: Building2 },
    { label: 'Pátios', to: `${prefix}/parking-lots`, icon: ParkingSquare },
    ...(canManageUsers() ? [{ label: 'Usuários', to: `${prefix}/users`, icon: Users }] : []),
  ];

  const navItems: NavItem[] = (isPlatformAdmin()
    ? (tenantSlug 
        ? [
            ...platformNav,
            { label: 'Empresas', to: `${prefix}/companies`, icon: Building2 },
            { label: 'Pátios', to: `${prefix}/parking-lots`, icon: ParkingSquare },
          ] 
        : platformNav
      )
    : isClientAdmin()
      ? adminNav
      : isCompanyAdmin()
        ? companyAdminNav
        : operationsNav) as NavItem[];

  return (
    <aside
      className={cn(
        'flex flex-col bg-primary-600 text-white transition-all duration-300',
        'h-full',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      {/* Logo */}
      <div className={cn('flex items-center border-b border-primary-700/50 shrink-0', collapsed ? 'justify-center p-4' : 'px-6 py-5')}>
        {collapsed ? (
          <img src={simboloImg} alt="Arlo Auto" className="h-8 w-8 object-contain" />
        ) : (
          <img
            src={branding.logoUrl ?? logoImg}
            alt={branding.companyName}
            className="h-8 object-contain"
            style={{ maxWidth: '160px' }}
          />
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        <ul className="space-y-0.5">
          {navItems.map((item) => {
            return (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all',
                      isActive
                        ? 'bg-white/15 text-white'
                        : 'text-primary-100 hover:bg-white/10 hover:text-white',
                      collapsed && 'justify-center',
                    )
                  }
                  title={collapsed ? item.label : undefined}
                >
                  <item.icon className={cn('shrink-0', collapsed ? 'h-5 w-5' : 'h-4 w-4')} />
                  {!collapsed && <span>{item.label}</span>}
                  {!collapsed && item.badge !== undefined && (
                    <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-brand-amber text-[10px] font-bold">
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Settings */}
      <div className="border-t border-primary-700/50 p-2">
        <NavLink
          to={isPlatformAdmin() && !tenantSlug ? '/admin/settings' : `${prefix}/settings`}
          className={cn(
            'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-primary-100 hover:bg-white/10 hover:text-white transition-all',
            collapsed && 'justify-center',
          )}
          title={collapsed ? 'Configurações' : undefined}
        >
          <Settings className={cn('shrink-0', collapsed ? 'h-5 w-5' : 'h-4 w-4')} />
          {!collapsed && 'Configurações'}
        </NavLink>
      </div>
    </aside>
  );
}
